import React from "react";
import { useMediaQuery } from "@material-ui/core";
import styled from "styled-components";

import { addOpacity, breakpoints, color } from "style/constants";
import { BHIHeader, BHILabel, BHIScore } from "./styled";
import StyledGrid from "../StyledGrid";
import { PillarName } from "models/score";

const StyledFactorHeader = styled(StyledGrid)<{
  factor?: string;
  selected?: string;
}>`
  cursor: pointer;
  transition: 0.3s;
  color: ${props => getColor(props.factor, props.selected)};
  box-shadow: ${props =>
    props.factor === props.selected ? "0px -3px 3px rgba(50, 50, 50, 0.75)" : "none"};
  &:hover {
    box-shadow: 0px -3px 3px rgba(50, 50, 50, 0.75);
  }
`;

const getColor = (factor?: string, selected?: string) => {
  if (selected === "") return color.BLACK;
  else if (factor === selected) return color.DARKBLUE;
  else return addOpacity(color.BLACK, 0.6);
};

interface Props {
  clarity: number;
  emotionalBalance: number;
  index: number;
  indexDelta?: number;
  connectedness: number;
  setSelectedFactor: React.Dispatch<React.SetStateAction<PillarName | undefined>>;
  selectedFactor?: PillarName;
}

const FactorsBanner: React.FC<Props> = ({
  clarity,
  emotionalBalance,
  index,
  indexDelta,
  connectedness,
  setSelectedFactor,
  selectedFactor
}) => {
  const mobile = useMediaQuery(`(max-width:${breakpoints.lg}px)`);

  const getFactorColor = (factor: PillarName): color => {
    if (factor === selectedFactor || !selectedFactor) {
      switch (factor) {
        case PillarName.EMOTIONAL_BALANCE:
          return color.FUSCHIA;
        case PillarName.CONNECTEDNESS:
          return color.GREEN;
        case PillarName.CLARITY:
          return color.PURPLE;
        case PillarName.NEW_INDEX:
          return color.YELLOW;
        case PillarName.EMERGING_FACTOR:
          return color.YELLOW;
        default:
          return color.GRAY_PROGESS_BAR;
      }
    } else return color.GRAY_PROGESS_BAR;
  };

  return (
    <StyledGrid
      alignItems="center"
      backgroundColor={color.WHITE}
      container
      direction="column"
      padding={!mobile ? "1rem" : ""}
    >
      <StyledGrid alignItems="center" item container maxWidth="686px" position="relative" xs={12}>
        <StyledGrid item xs={10} lg={12}>
          <BHIHeader>
            Your BrainHealth® Index <strong>{index}</strong>
          </BHIHeader>
        </StyledGrid>
        {/* {indexDelta && (
          <StyledGrid
            item
            textAlign="end"
            xs={2}
            position={mobile ? "" : "absolute"}
            right={mobile ? "" : "0"}
          >
            <ScoreDelta>
              <Arrow className={indexDelta < 0 ? "negative" : ""} />
              <p>{Math.abs(indexDelta)}</p>
            </ScoreDelta>
          </StyledGrid>
        )} */}
      </StyledGrid>
      <StyledGrid
        alignItems="flex-start"
        container
        item
        maxWidth="686px"
        textAlign="center"
        xs={12}
      >
        <StyledFactorHeader
          item
          container
          direction="column"
          xs={3}
          selected={selectedFactor}
          factor={"emotionalBalance"}
          onClick={() =>
            setSelectedFactor(
              selectedFactor === PillarName.EMOTIONAL_BALANCE
                ? undefined
                : PillarName.EMOTIONAL_BALANCE
            )
          }
        >
          <StyledGrid
            backgroundColor={getFactorColor(PillarName.EMOTIONAL_BALANCE)}
            height="6px"
            item
          />
          <StyledGrid item>
            <BHILabel>Emotional Balance</BHILabel>
          </StyledGrid>
          <StyledGrid item>
            <BHIScore>{emotionalBalance >= 50 ? emotionalBalance : 63}</BHIScore>
          </StyledGrid>
        </StyledFactorHeader>
        <StyledFactorHeader
          item
          container
          direction="column"
          xs={3}
          selected={selectedFactor}
          factor={"connectedness"}
          onClick={() =>
            setSelectedFactor(
              selectedFactor === PillarName.CONNECTEDNESS ? undefined : PillarName.CONNECTEDNESS
            )
          }
        >
          <StyledGrid
            backgroundColor={getFactorColor(PillarName.CONNECTEDNESS)}
            height="6px"
            item
          />
          <StyledGrid item>
            <BHILabel>Connectedness</BHILabel>
          </StyledGrid>
          <StyledGrid item>
            <BHIScore>{connectedness >= 50 ? connectedness : 57}</BHIScore>
          </StyledGrid>
        </StyledFactorHeader>
        <StyledFactorHeader
          item
          container
          direction="column"
          xs={3}
          selected={selectedFactor}
          factor={"clarity"}
          onClick={() =>
            setSelectedFactor(
              selectedFactor === PillarName.CLARITY ? undefined : PillarName.CLARITY
            )
          }
        >
          <StyledGrid backgroundColor={getFactorColor(PillarName.CLARITY)} height="6px" item />
          <StyledGrid item>
            <BHILabel>Clarity</BHILabel>
          </StyledGrid>
          <StyledGrid item>
            <BHIScore>{clarity >= 50 ? clarity : 71}</BHIScore>
          </StyledGrid>
        </StyledFactorHeader>
        <StyledFactorHeader
          item
          container
          direction="column"
          xs={3}
          selected={selectedFactor}
          factor={"emergingFactor"}
          onClick={() =>
            setSelectedFactor(
              selectedFactor === PillarName.EMERGING_FACTOR ? undefined : PillarName.EMERGING_FACTOR
            )
          }
        >
          <StyledGrid
            backgroundColor={getFactorColor(PillarName.EMERGING_FACTOR)}
            height="6px"
            item
          />
          <StyledGrid item padding="0 1rem">
            <BHILabel className="emerging">Emerging Factor</BHILabel>
          </StyledGrid>
        </StyledFactorHeader>
      </StyledGrid>
    </StyledGrid>
  );
};

export default FactorsBanner;
